import { AbstractControl, FormGroup } from '@angular/forms';
import { PERSON_NAME_INVALID_MESSAGE } from './person-name.validators';

export interface FormFieldErrorOptions {
  /** Override message for personName / minlength errors. */
  personNameInvalidMessage?: string;
  /** Per-control override for pattern errors (e.g. phoneNumber). */
  patternMessages?: Readonly<Record<string, string>>;
}

/**
 * True when a control should show invalid UI (blur/submit marked it touched).
 */
export function isFormFieldInvalid(
  form: FormGroup | null | undefined,
  controlName: string
): boolean {
  const ctrl = form?.get(controlName);
  return !!ctrl && ctrl.touched && ctrl.invalid;
}

/**
 * First applicable inline error for a touched invalid control.
 * Pass FIELD_LABELS so new fields only need a label entry + validators.
 */
export function getFormFieldErrorMessage(
  form: FormGroup | null | undefined,
  controlName: string,
  fieldLabels: Readonly<Record<string, string>>,
  options: FormFieldErrorOptions = {}
): string | null {
  const ctrl = form?.get(controlName);
  if (!ctrl || !ctrl.touched || !ctrl.errors) {
    return null;
  }
  return resolveFormFieldErrorMessage(controlName, ctrl, fieldLabels, options);
}

export function resolveFormFieldErrorMessage(
  controlName: string,
  ctrl: AbstractControl,
  fieldLabels: Readonly<Record<string, string>>,
  options: FormFieldErrorOptions = {}
): string | null {
  const errors = ctrl.errors;
  if (!errors) {
    return null;
  }

  const label = fieldLabels[controlName] ?? controlName;

  if (errors['required']) {
    return `${label} is required.`;
  }
  if (errors['personName'] || errors['minlength']) {
    return options.personNameInvalidMessage ?? PERSON_NAME_INVALID_MESSAGE;
  }
  if (errors['email']) {
    return 'Enter a valid email address.';
  }
  if (errors['maxlength']) {
    const max = errors['maxlength']?.requiredLength;
    return max != null
      ? `${label} must be ${max} characters or fewer.`
      : `${label} is too long.`;
  }
  if (errors['pattern']) {
    return (
      options.patternMessages?.[controlName] ?? `${label} is invalid.`
    );
  }
  return null;
}
